import { ExternalLink, FileText } from 'lucide-react'
import { Link } from 'react-router-dom'
import { SendRfqButton } from '../../components/SendRfqButton'
import { EmptyState, ErrorState, LoadingState } from '../../components/States'
import { StatusBadge } from '../../components/StatusBadge'
import { useApi } from '../../hooks/useApi'
import { api } from '../../lib/apiClient'
import { formatDate } from '../../lib/formatters'
import type { SupplierRfqView } from './types'

export function OpportunitySupplierRfqsSection({ opportunityId }: { opportunityId: string }) {
  const { data, loading, error, reload } = useApi(
    () => api.related<SupplierRfqView>('opportunities', opportunityId, 'supplier-rfqs'),
    [opportunityId],
  )
  const rfqs = data ?? []
  return (
    <section className="card mt-7 overflow-hidden">
      <div className="flex flex-col justify-between gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-[.14em] text-pine-600">Supplier sourcing</div>
          <h2 className="mt-1 text-xl font-bold text-slate-950">Supplier RFQs</h2>
          <p className="mt-1 text-sm text-slate-500">One supplier Quote per supplier, all using the same reusable Pricing Item set.</p>
        </div>
        <SendRfqButton opportunityId={opportunityId} onComplete={reload} />
      </div>
      {loading ? <LoadingState label="Loading supplier RFQs…" /> : error ? <ErrorState message={error} onRetry={reload} /> : rfqs.length ? (
        <div className="divide-y divide-slate-100">
          {rfqs.map((rfq) => (
            <article key={rfq.quoteId} className="flex flex-col justify-between gap-4 px-5 py-4 sm:flex-row sm:items-center">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2"><Link to={`/quotes/${rfq.quoteId}`} className="font-mono text-sm font-bold text-pine-700 hover:underline">{rfq.quoteCode}</Link><StatusBadge value={rfq.status} /></div>
                <div className="mt-1 font-bold text-slate-900">{rfq.supplier.name}</div>
                <p className="mt-1 text-sm text-slate-500">{rfq.itemCount} Pricing Item{rfq.itemCount === 1 ? '' : 's'} · {rfq.sentAt ? `Sent ${formatDate(rfq.sentAt)}` : `Draft created ${formatDate(rfq.createdAt)}`}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {rfq.latestRfq && <a href={rfq.latestRfq.url} target="_blank" rel="noreferrer" className="btn-secondary"><FileText className="h-4 w-4" />Open PDF<ExternalLink className="h-3.5 w-3.5" /></a>}
                <Link to={`/quotes/${rfq.quoteId}`} className="btn-secondary">Open Quote</Link>
              </div>
            </article>
          ))}
        </div>
      ) : <EmptyState title="No supplier RFQs yet" description="Prepare pricing, then create draft requests for one or more suppliers." />}
    </section>
  )
}
